import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useCallback } from "react";

import { $featureFlags, Flag, defaultFlags } from "../../internal/feature-flags";
import { store } from "../../internal/store";

export { Flag };

/**
 * Access all feature flags
 */
export const useFeatureFlags = () => {
	const flags = useAtomValue(loadable($featureFlags));
	const setFlagAtom = useSetAtom($featureFlags);

	const setFlag = useCallback(
		async (flagId: Flag, enabled: boolean) => {
			await setFlagAtom([flagId, enabled]);
		},
		[setFlagAtom]
	);

	return {
		flags: flags.state === "hasData" ? flags.data : defaultFlags,
		setFlag,
	};
};

/**
 * Check if a specific feature flag is enabled
 */
export const useIsFeatureFlagEnabled = (flagId: Flag) => {
	const { flags } = useFeatureFlags();

	return flags[flagId] === true;
};

/**
 * Get a specific feature flag value
 */
export const useFeatureFlag = (flagId: Flag) => {
	const { flags } = useFeatureFlags();

	return flags[flagId];
};

/**
 * Check if a specific feature flag is enabled outside of React context.
 */
export const getFeatureFlag = (flagId: Flag): boolean => {
	const flags = store.get(loadable($featureFlags));
	if (flags.state === "hasData") {
		return flags.data[flagId] === true;
	}
	return defaultFlags[flagId];
};
